import { useEffect, useState, useMemo } from 'react';
import Oidc from 'oidc-client';
import { manager } from '../stores/user/UserStore';

export interface IUserHookResult {
  user: Oidc.User | undefined;
  isSuperAdmin: boolean;
}

/**
 * Custom hook for accessing the OIDC user object.
 * It encapsulates the logic for retrieving the user from the oidc-client manager.
 * @returns An object with the OIDC user object and an isSuperAdmin flag.
 */
export const useUser = (): IUserHookResult => {
  const [user, setUser] = useState<Oidc.User | undefined>();

  useEffect(() => {
    const fetchUser = async () => {
      const userFromManager = await manager.getUser();
      setUser(userFromManager ?? undefined);
    };

    const onUserLoaded = (loadedUser: Oidc.User) => {
      setUser(loadedUser);
    };

    const onUserUnloaded = () => {
      setUser(undefined);
    };

    manager.events.addUserLoaded(onUserLoaded);
    manager.events.addUserUnloaded(onUserUnloaded);

    fetchUser();

    return () => {
      manager.events.removeUserLoaded(onUserLoaded);
      manager.events.removeUserUnloaded(onUserUnloaded);
    };
  }, []);

  const isSuperAdmin = useMemo(() => (
    user?.profile?.ApplicationId === "2" &&
    ["g@randmar.io", "georges@randmar.io", "d@randmar.io", "mia@randmar.io"].includes(user?.profile?.ApplicationName ?? "")
  ), [user]);

  return { user, isSuperAdmin };
};
